import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Pagination, PaginationProps, Tag, Button, Dropdown } from "antd";
import Filters from "../../components/Filters";
import { Filter } from "../../interfaces/Filter";
import { LabOrder, LabOrderStatus } from "../../interfaces/LabOrder";
import { LanguageContext } from "../../providers/LanguageProvider";
import { format } from "date-fns";
import Modal from "../../components/Modal";
import { useNotification } from "../../NotificationContext";
import AddLabOrder from "./AddLabOrder";
import { getLabOrders, updateLabOrderStatus } from "../../apis/LabOrder";
import PaymentModal from "../../components/PaymentModal";
import CommonPagination from "../../components/CommanPagination";

const labOrderFilters: Array<Filter> = [
  {
    label: "Order Number",
    key: "order_number",
    value: "",
    component: "input",
  },
  {
    label: "Patient Name",
    key: "patient_name",
    value: "",
    component: "input",
  },
  {
    label: "Status",
    key: "status",
    value: "",
    component: "input",
  },
  // {
  //   label: "Date",
  //   key: "date",
  //   value: "",
  //   component: "date",
  // },
];

const LabOrderList: React.FC = () => {
  const { translate: t } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { search } = useLocation();
  const [labOrders, setLabOrders] = useState<Array<LabOrder>>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState<number | null>(null);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [filters] = useState<Array<Filter>>(labOrderFilters);
  const [isClearFilterList] = useState(false);
  const { showNotification } = useNotification();
  const [, setSearchParams] = useSearchParams();
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const [meta, setMeta] = useState<any>({
    currentPage: 1,
    perPage: 10,
    total: 0,
    // total: sampleLabOrders.length,
  });

  const fetchLabOrders = async (params: any = { page: 1, per_page: 10 }) => {
    try {
      const res = await getLabOrders(params);
      setLabOrders(res.data.data);
      setMeta(res.data.meta);
    } catch (err) {
      console.error("Error fetching lab orders", err);
    }
  };

  useEffect(() => {
    if (search) {
      const filters = JSON.parse(
        '{"' +
          decodeURI(search.substring(1))
            .replace(/\+/g, " ")
            .replace(/"/g, '\\"')
            .replace(/&/g, '","')
            .replace(/=/g, '":"') +
          '"}'
      );
      fetchLabOrders({ per_page: 10, ...filters });
    } else {
      fetchLabOrders({ per_page: 10 });
    }
  }, [search]);

  // const handleFilters = (params: any) => {
  //   if (params.length > 0) {
  //     let updatedQueryParams = new URLSearchParams();
  //     params.forEach((i: any) => {
  //       updatedQueryParams.set(i.key, i.value);
  //     });
  //     setSearchParams(updatedQueryParams.toString());
  //   } else {
  //     navigate(`/laboratory`);
  //   }
  // };

  const handleFilters = (params: any) => {
    if (params.length > 0) {
      const updatedQueryParams = new URLSearchParams();
      params.forEach((i: any) => updatedQueryParams.set(i.key, i.value));
      setSearchParams(updatedQueryParams.toString());
    } else {
      setSearchParams("");
      fetchLabOrders({ page: 1, per_page: 10 });
    }
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    fetchLabOrders();
  };

  const handleViewDetail = (orderId: number) => {
    navigate(`/lab-orders/view/${orderId}`);
  };

  const cancelLabOrder = async (id: number) => {
    try {
      await updateLabOrderStatus(id, "cancelled");
    } catch (error) {
      console.error("Cancel API Error:", error);
      throw error;
    }
  };
  const handleEditOrder = (orderId: number) => {
    navigate(`/lab-orders/edit/${orderId}`);
  };

  const getStatusColor = (status: LabOrderStatus) => {
    switch (status) {
      case "ordered":
        return "red";
      case "in_progress":
        return "blue";
      case "completed":
        return "green";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case "stat":
        return "red";
      case "routine":
        return "blue";
      default:
        return "default";
    }
  };

  const getPatientIdByOrderId = (orderId: number) => {
    const order = labOrders.find((o) => o.id === orderId);
    return order?.patientId;
  };

  const selectedPatientOrders = labOrders.filter((order) =>
    selectedOrders.includes(order.id!)
  );

  const selectedTestNames = selectedPatientOrders.map(
    (order) => order.labTest?.name || "-"
  );

  const patientId = selectedPatientOrders[0]?.patientId;
  const sourceId = selectedPatientOrders[0]?.id;

  const totalAmount = selectedPatientOrders.reduce(
    (sum, order) => sum + parseFloat((order.charge as string) || "0"),
    0
  );

  const totalDiscount = selectedPatientOrders.reduce(
    (sum, order) => sum + parseFloat((order.discount as string) || "0"),
    0
  );

  const selectedPatientName = (() => {
    const selected = labOrders.filter((o) => selectedOrders.includes(o.id!));
    if (selected.length === 0) return "Counter Sale";

    const patient = selected[0].patient;
    return patient
      ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
      : "Counter Sale";
  })();

  const toggleOrder = (id: number) => {
    setSelectedOrders((prev) => {
      const isAlreadySelected = prev.includes(id);

      let updatedSelection = isAlreadySelected
        ? prev.filter((x) => x !== id)
        : [...prev, id];

      const patientIds = updatedSelection
        .map((orderId) => getPatientIdByOrderId(orderId))
        .filter(Boolean);
      const uniquePatients = Array.from(new Set(patientIds));

      if (uniquePatients.length > 1) {
        showNotification(
          "error",
          "Invalid Selection",
          "You have selected orders of different patients",
          "topRight"
        );
        return prev;
      }
      return updatedSelection;
    });
  };
  const resetSelectionAndClose = () => {
    setIsPaymentModalOpen(false);
    setSelectedOrders([]);
  };

  const getTestName = (test: any) => test?.labTest?.name || test?.name || "-";

  // const onPaginationChange: PaginationProps["onChange"] = (page, pageSize) => {
  //   fetchLabOrders({ page, per_page: pageSize });
  // };
  const onPaginationChange = (page: number, pageSize?: number) => {
  fetchLabOrders({ page, per_page: pageSize || 10 }); 
};

  const ordersWithTests = labOrders.map((order) => ({
    order,
    item: order.labTest,
    key: `${order.id}-${order.labTest?.id}`,
  }));

  const orderAmounts = selectedPatientOrders.reduce((acc, order) => {
    // Calculate actual amount: charge - discount
    const orderAmount =
      parseFloat((order.charge as string) || "0") -
      parseFloat((order.discount as string) || "0");
    acc[order.id!] = orderAmount;
    return acc;
  }, {} as Record<number, number>);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex lg:hidden grow">
        <div className="flex grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Laboratory Orders
          </h1>
        </div>
        <div className="flex">
          <Filters
            clearFilterList={isClearFilterList}
            filters={filters}
            fetchList={(appliedFilters: any) => handleFilters(appliedFilters)}
          />
        </div>
      </div>
      <div className="hidden lg:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Laboratory Orders
          </h1>
        </div>
        <div className="mx-2 mt-4 sm:mt-0 sm:ml-16 sm:flex">
          <div className="mr-2">
            <Filters
              clearFilterList={isClearFilterList}
              filters={filters}
              fetchList={(appliedFilters: any) => handleFilters(appliedFilters)}
            />
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-hover"
          >
            + New Lab Order
          </button>
        </div>
      </div>

      {/* Mobile Add Button */}
      <div className="mt-4 lg:hidden">
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="px-4 py-2 w-full text-sm font-semibold text-white bg-primary rounded-md hover:bg-hover"
        >
          + New Lab Order
        </button>
      </div>

      {selectedOrders.length > 0 && (
        <div className="flex justify-start mb-3">
          <button
            onClick={() => setIsPaymentModalOpen(true)}
            type="button"
            className="px-3 py-1 text-white rounded bg-primary hover:bg-hover"
          >
            Make Payment
          </button>
        </div>
      )}

      {/* Lab Orders List */}
      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 ring-1 ring-black ring-opacity-5 shadow md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Order No.
                    </th>
                    {/* <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Sample Id.
                    </th> */}
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Patient
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Test
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Priority
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Status
                    </th>
                    {/* <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Payment Status
                    </th> */}
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ordersWithTests.length > 0 ? (
                    ordersWithTests.map(({ order, item, key }) => (
                      <tr key={key} className="hover:bg-gray-50">
                        {/* <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6">
                          {order.orderNumber}
                        </td> */}
                        <td className="px-3 py-4 text-sm text-gray-900">
                          <div className="flex gap-2 items-start">
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.id!)}
                              onChange={() => toggleOrder(order.id!)}
                              className="mt-1 w-4 h-4 rounded border-gray-300 cursor-pointer text-primary"
                            />

                            <div>
                              <div className="font-medium">
                                {order.orderNumber}
                              </div>
                              {order.createdAt && (
                                <div className="flex gap-2">

                                <div className="text-xs text-gray-500">
                                  {format(
                                    new Date(order.createdAt),
                                    "dd MMM yyyy"
                                  )}
                                
                                  </div>
                                  {order.visitId &&(

<Tag color="blue">IPD</Tag>
)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-4 text-sm text-gray-900">
                          <div>
                            <div className="font-medium">
                              {order.patient
                                ? `${order.patient.firstName || ""} ${
                                    order.patient.lastName || ""
                                  }`.trim() || `Patient #${order.patientId}`
                                : `Patient #${order.patientId}`}
                            </div>
                            {order.patient?.phone && (
                              <div className="text-xs text-gray-500">
                                {order.patient.phone}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-4 text-sm text-gray-900">
                          <div>
                            <div className="font-medium text-gray-800">
                              {getTestName(item)}
                            </div>
                            {order.sampleId && (
                              <div className="text-xs text-gray-500">
                                {order.sampleId}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {order?.priority && (
                            <Tag color={getPriorityColor(order?.priority)}>
                              {order?.priority?.toUpperCase()}
                            </Tag>
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm whitespace-nowrap">
                          <Tag
                            color={getStatusColor(
                              order.status as LabOrderStatus
                            )}
                          >
                            {order.status?.toUpperCase()}
                          </Tag>
                        </td>

                        <td className="px-4 py-4 text-sm text-gray-900">
                          {order.status?.toLowerCase() === "cancelled" ? (
                            <Button disabled>Actions</Button>
                          ) : (
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: "edit",
                                    label: (
                                      <span
                                        onClick={() => {
                                          if (
                                            order.status?.toLowerCase() !==
                                              "completed" &&
                                            order.id
                                          ) {
                                            handleEditOrder(order.id);
                                          }
                                        }}
                                        className={
                                          order.status?.toLowerCase() ===
                                          "completed"
                                            ? "cursor-not-allowed text-gray-400"
                                            : "cursor-pointer"
                                        }
                                      >
                                        Add Result
                                      </span>
                                    ),
                                    disabled:
                                      order.status?.toLowerCase() ===
                                      "completed",
                                  },
                                  {
                                    key: "view",
                                    label: (
                                      <span
                                        onClick={() =>
                                          order.id && handleViewDetail(order.id)
                                        }
                                        className="cursor-pointer"
                                      >
                                        View
                                      </span>
                                    ),
                                  },
                                  ...(order.status?.toLowerCase() === "completed"
                                  && !order.visitId
                                  // order.paymentStatus?.toLowerCase() !== "paid"
                                    ? [
                                      {
                                        key: "make-payment",
                                        label: (
                                          <span
                                            onClick={() => {
                                              setSelectedOrders([order.id!]);
                                              setIsPaymentModalOpen(true);
                                            }}
                                            className="cursor-pointer text-gray"
                                          >
                                            Make Payment
                                          </span>
                                        ),
                                      },
                                    ]
                                    : []),
                                  {
                                    key: "cancel",
                                    label: (
                                      <span
                                        onClick={() => {
                                          if (
                                            order.status?.toLowerCase() !==
                                            "completed"
                                          ) {
                                            setCancelOrderId(order.id!);
                                            setIsCancelConfirmOpen(true);
                                          }
                                        }}
                                        className={
                                          order.status?.toLowerCase() ===
                                          "completed"
                                            ? "cursor-not-allowed text-gray-400"
                                            : "cursor-pointer"
                                        }
                                      >
                                        Cancel
                                      </span>
                                    ),
                                    disabled:
                                      order.status?.toLowerCase() ===
                                      "completed",
                                  },
                                ],
                              }}
                              placement="bottomLeft"
                            >
                              <Button>Actions</Button>
                            </Dropdown>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-4 text-sm text-center text-gray-500"
                      >
                        No lab orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CommonPagination meta={meta} onChange={onPaginationChange} />

      {/* Modals */}
      {isAddModalOpen && (
        <Modal
          modalContent={
            <AddLabOrder
              orderId={null}
              closeModal={handleCloseModal}
              onOrderCreated={(newOrder) =>
                setLabOrders((prev) => [newOrder, ...prev])
              }
            />
          }
        />
      )}

      {isPaymentModalOpen && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={resetSelectionAndClose}
          total={totalAmount}
          discount={totalDiscount}
          patientId={patientId ?? 0}
          sourceType="pathology"
          sourceId={sourceId ?? 0}
          patientName={selectedPatientName}
          testNames={selectedTestNames}
          selectedOrderIds={selectedOrders}
          orderAmounts={orderAmounts}
          onPaymentSuccess={() => {
            // Refresh the lab orders list after successful payment
            fetchLabOrders({ page: meta.currentPage, per_page: meta.perPage });
            // Reset selections
            setSelectedOrders([]);
          }}
        />
      )}

      {isCancelConfirmOpen && (
        <Modal
          modalContent={
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Cancel Order
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Are you sure you want to cancel this order?
              </p>
              <div className="flex gap-3 justify-end items-center pt-4">
                <button
                  onClick={() => setIsCancelConfirmOpen(false)}
                  className="text-sm font-semibold text-gray-900 hover:text-gray-700"
                >
                  No
                </button>

                <button
                  onClick={async () => {
                    try {
                      await cancelLabOrder(cancelOrderId!);
                      fetchLabOrders();
                      setIsCancelConfirmOpen(false);
                    } catch (error) {
                      console.log("Cancel error:", error);
                    }
                  }}
                  className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dark"
                >
                  Yes, Cancel
                </button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};
export default LabOrderList;
